import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type Interaction from "../../../types/interaction/Interaction";
import type { TabId } from "../../components/DetailPane/DetailTabs";
import type { ColumnDef, ColumnRenderContext } from "./columnDefs";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TableRowProps {
  row: Interaction;
  isChecked: boolean;
  isViewing: boolean;
  initialTab: TabId;
  canBulkEdit: boolean;
  canExport: boolean;
  canReplay: boolean;
  visibleColumns: ColumnDef[];
  columnCtx: ColumnRenderContext;
  typeColumnDef: ColumnDef | undefined;
  onToggleCheckbox: (id: string) => void;
  onEdit: (id: string) => void;
  onDownloadFile: (id: string) => void;
  onDownloadTranscript: (id: string) => void;
  onInfo: (id: string) => void;
  onPlay: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TableRow = React.memo(function TableRow({ // NOSONAR
  row,
  isChecked,
  isViewing,
  initialTab,
  canBulkEdit,
  canExport,
  canReplay,
  visibleColumns,
  columnCtx,
  typeColumnDef,
  onToggleCheckbox,
  onEdit,
  onDownloadFile,
  onDownloadTranscript,
  onInfo,
  onPlay,
}: TableRowProps) {
  const { t } = useTranslation(["search", "common"]);

  return (
    <Box
      component="tr"
      sx={{
        backgroundColor: isViewing
          ? "var(--accent-dim) !important"
          : undefined,
        outline: isViewing ? "1px solid var(--accent-glow)" : "none",
        outlineOffset: -1,
      }}
    >
      {/* Checkbox — pinned */}
      <Box component="td" className="pinned-col" sx={{ textAlign: "center", width: 40, minWidth: 40, position: "sticky", left: 0, backgroundColor: isViewing ? "var(--accent-dim)" : "var(--bg-surface)", "tr:hover > &": { backgroundColor: "var(--bg-hover)" } }}>
        <Box
          component="input"
          type="checkbox"
          checked={isChecked}
          onChange={() => onToggleCheckbox(row.interactionId)}
          sx={{
            accentColor: "var(--accent)",
            width: 14,
            height: 14,
            cursor: "pointer",
            margin: "0 auto",
            display: "block",
          }}
        />
      </Box>

      {/* Type — pinned */}
      <Box component="td" className="pinned-col" sx={{ width: 70, minWidth: 70, position: "sticky", left: 40, backgroundColor: isViewing ? "var(--accent-dim)" : "var(--bg-surface)", "tr:hover > &": { backgroundColor: "var(--bg-hover)" } }}>
        {typeColumnDef?.render(row, columnCtx)}
      </Box>

      {/* Actions cell — pinned */}
      <Box
        component="td"
        className="pinned-col"
        sx={{
          textAlign: "center",
          width: 180,
          minWidth: 180,
          position: "sticky",
          left: 110,
          backgroundColor: isViewing ? "var(--accent-dim)" : "var(--bg-surface)",
          borderRight: "1px solid var(--border-subtle)",
          "tr:hover > &": { backgroundColor: "var(--bg-hover)" },
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          {/* Edit */}
          <Box
            component="button"
            onClick={() => onEdit(row.interactionId)}
            title={t("search:edit")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "6px",
              border: "none",
              background: "none",
              color: "var(--text-tertiary)",
              cursor: canBulkEdit ? "pointer" : "default",
              opacity: canBulkEdit ? 1 : 0.3,
              pointerEvents: canBulkEdit ? "auto" : "none",
              "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-raised)" },
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </Box>

          {/* Download File */}
          <Box
            component="button"
            onClick={() => onDownloadFile(row.interactionId)}
            title={t("common:downloadMedia", "Download Media")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "6px",
              border: "none",
              background: "none",
              color: "var(--text-tertiary)",
              cursor: canExport && row.fileName ? "pointer" : "default",
              opacity: canExport && row.fileName ? 1 : 0.3,
              pointerEvents: canExport && row.fileName ? "auto" : "none",
              "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-raised)" },
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </Box>

          {/* Download Transcript */}
          <Box
            component="button"
            onClick={() => onDownloadTranscript(row.interactionId)}
            title={t("common:downloadTranscript", "Download Transcript")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "6px",
              border: "none",
              background: "none",
              color: "var(--text-tertiary)",
              cursor: row.transcriptPath ? "pointer" : "default",
              opacity: row.transcriptPath ? 1 : 0.3,
              pointerEvents: row.transcriptPath ? "auto" : "none",
              "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-raised)" },
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </Box>

          {/* Info */}
          <Box
            component="button"
            onClick={() => onInfo(row.interactionId)}
            title={t("search:info")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "6px",
              border: "none",
              background: "none",
              color: isViewing && initialTab === "metadata"
                ? "var(--accent)"
                : "var(--text-tertiary)",
              cursor: "pointer",
              "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-raised)" },
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </Box>

          {/* Play */}
          <Box
            component="button"
            onClick={() => onPlay(row.interactionId)}
            title={t("search:play")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "6px",
              border: "none",
              background: "none",
              color: isViewing && initialTab === "transcript"
                ? "var(--accent)"
                : "var(--text-tertiary)",
              cursor: canReplay && row.hasMedia ? "pointer" : "default",
              opacity: canReplay && row.hasMedia ? 1 : 0.3,
              pointerEvents: canReplay && row.hasMedia ? "auto" : "none",
              "&:hover": { color: "var(--text-primary)", backgroundColor: "var(--bg-raised)" },
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <circle cx="12" cy="12" r="10" />
              <polygon points="10 8 16 12 10 16 10 8" />
            </svg>
          </Box>
        </Box>
      </Box>

      {/* Data cells in column order */}
      {visibleColumns.map((col) => (
        <Box
          component="td"
          key={col.field}
          style={{
            width: `var(--col-w-${col.field}, auto)`,
            minWidth: `var(--col-w-${col.field}, auto)`,
            maxWidth: `var(--col-w-${col.field}, none)`,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {col.render(row, columnCtx)}
        </Box>
      ))}
    </Box>
  );
});

export default TableRow;
